// The Board Ask's wire (EMC-WEB4 over the backend's EMC-BE9, `agent_docs/contracts/board-ask.md`):
// one shape read by the campaign's composer (Fundraisers, frame 21), the approvers' page (Board,
// frames 23 and 32) and the director's ask page (Board, frames 26 and 31).
//
// Hand-written through the configured `givmoClient` (the `/org` CRM precedent): the committed OpenAPI
// snapshot carries none of these operations yet. Every read is decoded field by field off
// `unknown`, the tolerant reader: a malformed field reads as absent, never a throw. Money stays the
// backend's decimal string.

import { givmoClient, isGivmoError } from '@givmo/api-client';

/** Where an ask stands. `gone` is an ask that has gone to the board, known by the instant it went. */
export type BoardAskState = 'draft' | 'submitted' | 'gone';

export interface BoardAskAttachment {
  collateralItemId: string;
  kind: 'image' | 'file';
  title: string;
  url: string;
  contentType: string;
  fileName: string;
  byteSize: number;
  datedOn: string | null;
}

export interface BoardAskCampaign {
  fundraiserId: string;
  name: string;
  status: string;
  publishedAt: string | null;
  endsAt: string | null;
  goalAmount: string | null;
  coverUrl: string | null;
  /** Whether a director can make a page: its fundraiser pages are on and open to anyone. */
  acceptsPages: boolean;
}

export interface BoardAskRecipient {
  boardMemberId: string;
  displayName: string;
  roleLabel: string | null;
}

export interface BoardAsk {
  id: string;
  fundraiserId: string;
  campaignName: string;
  campaign: BoardAskCampaign | null;
  factsAvailable: boolean;
  message: string;
  attachments: BoardAskAttachment[];
  offersShare: boolean;
  offersPage: boolean;
  state: BoardAskState;
  draftedByDisplayName: string | null;
  submittedAt: string | null;
  returnedAt: string | null;
  returnedByDisplayName: string | null;
  sentAt: string | null;
  sentByDisplayName: string | null;
  recipients: BoardAskRecipient[];
  canApprove: boolean;
}

export interface BoardAskForCampaign {
  ask: BoardAsk | null;
  /** The campaign as core answered it, ask or no ask; null where core could not be read. */
  campaign: BoardAskCampaign | null;
  recipients: BoardAskRecipient[];
  canApprove: boolean;
}

export interface BoardAskSummary {
  id: string;
  fundraiserId: string;
  campaignName: string;
  state: BoardAskState;
  draftedByDisplayName: string | null;
  submittedAt: string | null;
  sentAt: string | null;
  sentByDisplayName: string | null;
  recipientCount: number;
  emailedCount: number;
}

export interface BoardAskPage {
  supporterId: string | null;
  fundraiserId: string;
  status: string;
  raisedAmount: string;
  goalAmount: string | null;
  donorCount: number;
}

export interface BoardAskParticipant {
  boardMemberId: string;
  displayName: string;
  roleLabel: string | null;
  emailSent: boolean;
  notSentReason: string | null;
  openedAt: string | null;
  sharedAt: string | null;
  pageStartedAt: string | null;
  page: BoardAskPage | null;
}

export interface BoardAskParticipation {
  askId: string;
  campaignName: string;
  sentAt: string | null;
  sentByDisplayName: string | null;
  goalAmount: string | null;
  factsAvailable: boolean;
  participants: BoardAskParticipant[];
  linkGiftCount: number | null;
  linkGiftAmount: string | null;
}

export interface DirectorBoardAsk {
  askId: string;
  fundraiserId: string;
  campaignName: string;
  campaign: BoardAskCampaign | null;
  factsAvailable: boolean;
  organizationName: string | null;
  message: string;
  fromDisplayName: string | null;
  sentAt: string | null;
  attachments: BoardAskAttachment[];
  offersShare: boolean;
  offersPage: boolean;
  openedAt: string | null;
  sharedAt: string | null;
  pageStartedAt: string | null;
  page: BoardAskPage | null;
}

/** What the composer and the approvers save: the message, the items, what directors can do. */
export interface BoardAskDraftBody {
  message: string;
  collateral_item_ids: string[];
  offers_share: boolean;
  offers_page: boolean;
}

// ── The tolerant readers ─────────────────────────────────────────────────────

type Entry = Record<string, unknown>;

function isEntry(value: unknown): value is Entry {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function flag(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

function entries(value: unknown): Entry[] {
  return Array.isArray(value) ? value.filter(isEntry) : [];
}

function state(entry: Entry): BoardAskState | null {
  const value = entry.state;
  if (value === 'draft' || value === 'submitted') return value;
  return text(entry.sent_at) === null ? null : 'gone';
}

export function decodeAttachment(entry: Entry): BoardAskAttachment | null {
  const collateralItemId = text(entry.collateral_item_id);
  const title = text(entry.title);
  const url = text(entry.url);
  const kind = entry.kind === 'image' || entry.kind === 'file' ? entry.kind : null;
  if (collateralItemId === null || title === null || url === null || kind === null) return null;
  return {
    collateralItemId,
    kind,
    title,
    url,
    contentType: text(entry.content_type) ?? '',
    fileName: text(entry.file_name) ?? title,
    byteSize: count(entry.byte_size),
    datedOn: text(entry.dated_on),
  };
}

function attachments(value: unknown): BoardAskAttachment[] {
  return entries(value)
    .map(decodeAttachment)
    .filter((item): item is BoardAskAttachment => item !== null);
}

function campaign(value: unknown): BoardAskCampaign | null {
  if (!isEntry(value)) return null;
  const fundraiserId = text(value.fundraiser_id);
  const name = text(value.name);
  if (fundraiserId === null || name === null) return null;
  return {
    fundraiserId,
    name,
    status: text(value.status) ?? '',
    publishedAt: text(value.published_at),
    endsAt: text(value.ends_at),
    goalAmount: text(value.goal_amount),
    coverUrl: text(value.cover_url),
    acceptsPages: flag(value.accepts_pages),
  };
}

function recipients(value: unknown): BoardAskRecipient[] {
  const out: BoardAskRecipient[] = [];
  for (const entry of entries(value)) {
    const boardMemberId = text(entry.board_member_id);
    const displayName = text(entry.display_name);
    if (boardMemberId === null || displayName === null) continue;
    out.push({ boardMemberId, displayName, roleLabel: text(entry.role_label) });
  }
  return out;
}

function page(value: unknown): BoardAskPage | null {
  if (!isEntry(value)) return null;
  const fundraiserId = text(value.fundraiser_id);
  if (fundraiserId === null) return null;
  return {
    supporterId: text(value.supporter_id),
    fundraiserId,
    status: text(value.status) ?? '',
    raisedAmount: text(value.raised_amount) ?? '0.00',
    goalAmount: text(value.goal_amount),
    donorCount: count(value.donor_count),
  };
}

export function decodeBoardAsk(value: unknown): BoardAsk | null {
  if (!isEntry(value)) return null;
  const id = text(value.id);
  const fundraiserId = text(value.fundraiser_id);
  const askState = state(value);
  if (id === null || fundraiserId === null || askState === null) return null;
  return {
    id,
    fundraiserId,
    campaignName: text(value.campaign_name) ?? '',
    campaign: campaign(value.campaign),
    factsAvailable: flag(value.facts_available, true),
    message: typeof value.message === 'string' ? value.message : '',
    attachments: attachments(value.attachments),
    offersShare: flag(value.offers_share, true),
    offersPage: flag(value.offers_page, true),
    state: askState,
    draftedByDisplayName: text(value.drafted_by_display_name),
    submittedAt: text(value.submitted_at),
    returnedAt: text(value.returned_at),
    returnedByDisplayName: text(value.returned_by_display_name),
    sentAt: text(value.sent_at),
    sentByDisplayName: text(value.sent_by_display_name),
    recipients: recipients(value.recipients),
    canApprove: flag(value.can_approve),
  };
}

export function decodeBoardAskForCampaign(value: unknown): BoardAskForCampaign {
  if (!isEntry(value)) return { ask: null, campaign: null, recipients: [], canApprove: false };
  return {
    ask: decodeBoardAsk(value.ask),
    campaign: campaign(value.campaign),
    recipients: recipients(value.recipients),
    canApprove: flag(value.can_approve),
  };
}

export function decodeBoardAskList(value: unknown): BoardAskSummary[] {
  const out: BoardAskSummary[] = [];
  for (const entry of entries(value)) {
    const id = text(entry.id);
    const fundraiserId = text(entry.fundraiser_id);
    const askState = state(entry);
    if (id === null || fundraiserId === null || askState === null) continue;
    out.push({
      id,
      fundraiserId,
      campaignName: text(entry.campaign_name) ?? '',
      state: askState,
      draftedByDisplayName: text(entry.drafted_by_display_name),
      submittedAt: text(entry.submitted_at),
      sentAt: text(entry.sent_at),
      sentByDisplayName: text(entry.sent_by_display_name),
      recipientCount: count(entry.recipient_count),
      emailedCount: count(entry.emailed_count),
    });
  }
  return out;
}

export function decodeBoardAskParticipation(value: unknown): BoardAskParticipation | null {
  if (!isEntry(value)) return null;
  const askId = text(value.ask_id);
  if (askId === null) return null;
  const participants: BoardAskParticipant[] = [];
  for (const entry of entries(value.participants)) {
    const boardMemberId = text(entry.board_member_id);
    const displayName = text(entry.display_name);
    if (boardMemberId === null || displayName === null) continue;
    participants.push({
      boardMemberId,
      displayName,
      roleLabel: text(entry.role_label),
      emailSent: flag(entry.email_sent),
      notSentReason: text(entry.not_sent_reason),
      openedAt: text(entry.opened_at),
      sharedAt: text(entry.shared_at),
      pageStartedAt: text(entry.page_started_at),
      page: page(entry.page),
    });
  }
  return {
    askId,
    campaignName: text(value.campaign_name) ?? '',
    sentAt: text(value.sent_at),
    sentByDisplayName: text(value.sent_by_display_name),
    goalAmount: text(value.goal_amount),
    factsAvailable: flag(value.facts_available, true),
    participants,
    linkGiftCount: typeof value.link_gift_count === 'number' ? value.link_gift_count : null,
    linkGiftAmount: text(value.link_gift_amount),
  };
}

export function decodeDirectorBoardAsk(value: unknown): DirectorBoardAsk | null {
  if (!isEntry(value)) return null;
  const askId = text(value.ask_id);
  const fundraiserId = text(value.fundraiser_id);
  if (askId === null || fundraiserId === null) return null;
  return {
    askId,
    fundraiserId,
    campaignName: text(value.campaign_name) ?? '',
    campaign: campaign(value.campaign),
    factsAvailable: flag(value.facts_available, true),
    organizationName: text(value.organization_name),
    message: typeof value.message === 'string' ? value.message : '',
    fromDisplayName: text(value.from_display_name),
    sentAt: text(value.sent_at),
    attachments: attachments(value.attachments),
    offersShare: flag(value.offers_share, true),
    offersPage: flag(value.offers_page, true),
    openedAt: text(value.opened_at),
    sharedAt: text(value.shared_at),
    pageStartedAt: text(value.page_started_at),
    page: page(value.page),
  };
}

// ── The transport ────────────────────────────────────────────────────────────

function officer(orgRef: string): string {
  return `/board/${encodeURIComponent(orgRef)}`;
}

function campaignAsk(orgRef: string, fundraiserId: string): string {
  return `${officer(orgRef)}/campaigns/${encodeURIComponent(fundraiserId)}/ask`;
}

function ask(orgRef: string, askId: string): string {
  return `${officer(orgRef)}/asks/${encodeURIComponent(askId)}`;
}

function director(orgRef: string): string {
  return `/board-director/organizations/${encodeURIComponent(orgRef)}/asks`;
}

/** The ask family is dark (or the organization isn't the caller's): the route miss. */
export function isBoardAskUnavailable(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}

/**
 * The words of a refusal the person can act on (a 400 or 409 from the ask, its own plain sentence),
 * else `fallback`: retrying cures neither "This campaign isn't live" nor "Choose at least one".
 */
export function boardAskRefusal(error: unknown, fallback: string): string {
  if (!isGivmoError(error) || (error.status !== 400 && error.status !== 409)) return fallback;
  return error.entries[0]?.detail ?? fallback;
}

export const boardAskKeys = {
  campaign: (orgRef: string, fundraiserId: string) =>
    ['board-ask', 'campaign', orgRef, fundraiserId] as const,
  list: (orgRef: string) => ['board-ask', 'list', orgRef] as const,
  one: (orgRef: string, askId: string) => ['board-ask', 'one', orgRef, askId] as const,
  participation: (orgRef: string, askId: string) =>
    ['board-ask', 'participation', orgRef, askId] as const,
  director: (orgRef: string, askId: string) => ['board-ask', 'director', orgRef, askId] as const,
  directorList: (orgRef: string) => ['board-ask', 'director-list', orgRef] as const,
};

export async function fetchBoardAskForCampaign(
  orgRef: string,
  fundraiserId: string,
  signal?: AbortSignal,
): Promise<BoardAskForCampaign> {
  const { data } = await givmoClient.get({
    url: campaignAsk(orgRef, fundraiserId),
    signal,
    throwOnError: true,
  });
  return decodeBoardAskForCampaign(data);
}

function decoded(data: unknown): BoardAsk {
  const out = decodeBoardAsk(data);
  if (out === null) throw new Error('The Board Ask could not be read.');
  return out;
}

export async function saveBoardAskDraft(
  orgRef: string,
  fundraiserId: string,
  body: BoardAskDraftBody,
): Promise<BoardAsk> {
  const { data } = await givmoClient.put({
    url: campaignAsk(orgRef, fundraiserId),
    body,
    throwOnError: true,
  });
  return decoded(data);
}

export async function submitBoardAsk(orgRef: string, fundraiserId: string): Promise<BoardAsk> {
  const { data } = await givmoClient.post({
    url: `${campaignAsk(orgRef, fundraiserId)}/submit`,
    throwOnError: true,
  });
  return decoded(data);
}

export async function fetchBoardAsks(
  orgRef: string,
  signal?: AbortSignal,
): Promise<BoardAskSummary[]> {
  const { data } = await givmoClient.get({
    url: `${officer(orgRef)}/asks`,
    signal,
    throwOnError: true,
  });
  return decodeBoardAskList(data);
}

export async function fetchBoardAsk(
  orgRef: string,
  askId: string,
  signal?: AbortSignal,
): Promise<BoardAsk> {
  const { data } = await givmoClient.get({ url: ask(orgRef, askId), signal, throwOnError: true });
  return decoded(data);
}

export async function updateBoardAsk(
  orgRef: string,
  askId: string,
  body: BoardAskDraftBody,
): Promise<BoardAsk> {
  const { data } = await givmoClient.put({ url: ask(orgRef, askId), body, throwOnError: true });
  return decoded(data);
}

export async function sendBoardAsk(orgRef: string, askId: string): Promise<BoardAsk> {
  const { data } = await givmoClient.post({
    url: `${ask(orgRef, askId)}/send`,
    throwOnError: true,
  });
  return decoded(data);
}

export async function sendBackBoardAsk(orgRef: string, askId: string): Promise<BoardAsk> {
  const { data } = await givmoClient.post({
    url: `${ask(orgRef, askId)}/send-back`,
    throwOnError: true,
  });
  return decoded(data);
}

export async function fetchBoardAskParticipation(
  orgRef: string,
  askId: string,
  signal?: AbortSignal,
): Promise<BoardAskParticipation> {
  const { data } = await givmoClient.get({
    url: `${ask(orgRef, askId)}/participation`,
    signal,
    throwOnError: true,
  });
  const out = decodeBoardAskParticipation(data);
  if (out === null) throw new Error('Who’s taking part could not be read.');
  return out;
}

/** "Preview the email" (frame 23): the mail each director would get. Sends nothing. */
export async function fetchBoardAskEmailPreview(
  orgRef: string,
  askId: string,
  signal?: AbortSignal,
): Promise<{ subject: string; fromName: string; html: string }> {
  const { data } = await givmoClient.get({
    url: `${ask(orgRef, askId)}/email-preview`,
    signal,
    throwOnError: true,
  });
  const body = isEntry(data) ? data : {};
  return {
    subject: text(body.subject) ?? '',
    fromName: text(body.from_name) ?? '',
    html: text(body.html) ?? '',
  };
}

export async function fetchDirectorBoardAsk(
  orgRef: string,
  askId: string,
  signal?: AbortSignal,
): Promise<DirectorBoardAsk> {
  const { data } = await givmoClient.get({
    url: `${director(orgRef)}/${encodeURIComponent(askId)}`,
    signal,
    throwOnError: true,
  });
  const out = decodeDirectorBoardAsk(data);
  if (out === null) throw new Error('The Board Ask could not be read.');
  return out;
}

export async function markDirectorBoardAskOpened(orgRef: string, askId: string): Promise<void> {
  await givmoClient.post({
    url: `${director(orgRef)}/${encodeURIComponent(askId)}/opened`,
    throwOnError: true,
  });
}

export async function recordDirectorBoardAskAct(
  orgRef: string,
  askId: string,
  act: 'shared' | 'page_started',
): Promise<void> {
  await givmoClient.post({
    url: `${director(orgRef)}/${encodeURIComponent(askId)}/acts`,
    body: { act },
    throwOnError: true,
  });
}
